import React, { useState, useEffect } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Palette, Ruler, Type as TypeIcon, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { FONT_FAMILY_OPTIONS } from '../utils';
import { useLang } from '../i18n';

const ColorField = ({ label, value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testId}-picker`}
        className="h-9 w-10 cursor-pointer rounded-md border border-input bg-card p-1"
        aria-label={label}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="font-mono h-9 text-sm"
        maxLength={7}
        aria-label={`${label} (Hex)`}
      />
    </div>
  </div>
);

export const CustomizationPanel = ({ options, setOptions, isQr, supportsText, format, setFormat }) => {
  const { t } = useLang();
  const [advOpen, setAdvOpen] = useState(false);
  const upd = (patch) => setOptions((o) => ({ ...o, ...patch }));

  // Proportional text scaling: when enabled, derive the human-readable text
  // size from the barcode bar height so it keeps a fixed proportion.
  useEffect(() => {
    if (!isQr && options.scale_text) {
      const computed = Math.max(4, Math.min(40, Math.round(options.module_height * 0.65)));
      if (computed !== options.font_size) {
        upd({ font_size: computed });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQr, options.scale_text, options.module_height]);

  return (
    <div className="space-y-5">
      {/* Colors */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-sm font-medium">
          <Palette className="h-4 w-4 text-primary" />{t('colors')}
        </Label>
        <div className="grid grid-cols-2 gap-3">
          <ColorField label={t('foreground')} value={options.fg_color} onChange={(v) => upd({ fg_color: v })} testId="barcode-foreground-color" />
          <ColorField label={t('background')} value={options.bg_color} onChange={(v) => upd({ bg_color: v })} testId="barcode-background-color" />
        </div>
      </div>

      {/* Size */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-sm font-medium">
          <Ruler className="h-4 w-4 text-primary" />{t('size')}
        </Label>
        {isQr ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{t('qr_scale')}</span>
              <span className="font-mono text-xs text-foreground">{options.scale}</span>
            </div>
            <Slider aria-label={t('qr_scale')} value={[options.scale]} min={4} max={20} step={1} onValueChange={([v]) => upd({ scale: v })} data-testid="barcode-scale-slider" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t('bar_width')}</span>
                <span className="font-mono text-xs text-foreground">{options.module_width.toFixed(2)}</span>
              </div>
              <Slider aria-label={t('bar_width')} value={[options.module_width]} min={0.15} max={0.6} step={0.01} onValueChange={([v]) => upd({ module_width: v })} data-testid="barcode-width-slider" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t('bar_height')}</span>
                <span className="font-mono text-xs text-foreground">{Math.round(options.module_height)}</span>
              </div>
              <Slider aria-label={t('bar_height')} value={[options.module_height]} min={8} max={40} step={1} onValueChange={([v]) => upd({ module_height: v })} data-testid="barcode-height-slider" />
            </div>
          </div>
        )}
      </div>

      {/* Show text (1D) */}
      {!isQr && (
        <div className="flex items-center justify-between rounded-lg border bg-secondary/40 px-3 py-2.5">
          <Label htmlFor="show-text" className="flex items-center gap-2 text-sm">
            <TypeIcon className="h-4 w-4 text-primary" />{t('show_text')}
          </Label>
          <Switch
            id="show-text"
            checked={options.show_text}
            onCheckedChange={(v) => upd({ show_text: v })}
            disabled={!supportsText}
            data-testid="barcode-show-text-switch"
          />
        </div>
      )}

      {/* Format */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">{t('output_format')}</Label>
        <Select value={format} onValueChange={setFormat}>
          <SelectTrigger aria-label={t('output_format')} data-testid="barcode-format-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="png">PNG</SelectItem>
            <SelectItem value="svg">SVG</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Advanced */}
      <Collapsible open={advOpen} onOpenChange={setAdvOpen}>
        <CollapsibleTrigger asChild>
          <button
            data-testid="barcode-advanced-toggle"
            className="flex w-full items-center justify-between rounded-lg border border-dashed px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-secondary/40 hover:text-foreground"
          >
            <span className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" />{t('advanced')}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${advOpen ? 'rotate-180' : ''}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-3">
          <p className="text-xs text-muted-foreground">{t('advanced_hint')}</p>
          {!isQr && supportsText && options.show_text && (
            <>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <TypeIcon className="h-3.5 w-3.5 text-primary" />{t('font_family')}
                </Label>
                <Select value={options.font_family || 'sans'} onValueChange={(v) => upd({ font_family: v })}>
                  <SelectTrigger aria-label={t('font_family')} data-testid="barcode-font-family-select" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FONT_FAMILY_OPTIONS.map((f) => (
                      <SelectItem key={f.id} value={f.id} data-testid={`barcode-font-family-${f.id}`}>{t(f.key)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border bg-secondary/40 px-3 py-2.5">
                <Label htmlFor="bold-text" className="flex items-center gap-2 text-sm">
                  <TypeIcon className="h-4 w-4 text-primary" />{t('bold_text')}
                </Label>
                <Switch
                  id="bold-text"
                  checked={!!options.font_bold}
                  onCheckedChange={(v) => upd({ font_bold: v })}
                  data-testid="barcode-bold-text-switch"
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border bg-secondary/40 px-3 py-2.5">
                <Label htmlFor="scale-text" className="flex items-center gap-2 text-sm">
                  <Ruler className="h-4 w-4 text-primary" />{t('scale_text')}
                </Label>
                <Switch
                  id="scale-text"
                  checked={!!options.scale_text}
                  onCheckedChange={(v) => upd({ scale_text: v })}
                  data-testid="barcode-scale-text-switch"
                />
              </div>
            </>
          )}
          {!isQr && supportsText && options.show_text && !options.scale_text && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t('text_size')}</span>
                <span className="font-mono text-xs text-foreground">{options.font_size}</span>
              </div>
              <Slider aria-label={t('text_size')} value={[options.font_size]} min={4} max={24} step={1} onValueChange={([v]) => upd({ font_size: v })} data-testid="barcode-font-size-slider" />
            </div>
          )}
          {isQr ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t('qr_border')}</span>
                <span className="font-mono text-xs text-foreground">{options.border}</span>
              </div>
              <Slider aria-label={t('qr_border')} value={[options.border]} min={0} max={10} step={1} onValueChange={([v]) => upd({ border: v })} data-testid="barcode-border-slider" />
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t('qr_border')}</span>
                <span className="font-mono text-xs text-foreground">{options.quiet_zone.toFixed(1)}</span>
              </div>
              <Slider aria-label={t('qr_border')} value={[options.quiet_zone]} min={0} max={20} step={0.5} onValueChange={([v]) => upd({ quiet_zone: v })} data-testid="barcode-quietzone-slider" />
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
